import { Component, EventEmitter, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NgControl } from '@angular/forms';
import { NgbDateStruct, NgbDatepicker, NgbDatepickerNavigateEvent, NgbPopover, NgbPopoverConfig, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { noop } from 'rxjs';
import { getRandomId } from 'src/app/shared/extensions/string.extensions';
import { DateTimePickerType } from './date-time-picker-type';
import { DateTimeModel } from './date-time.model';

@Component({
    selector: 'app-standard-datetime-picker',
    templateUrl: './standard-datetime-picker.component.html',
    styleUrls: ['./standard-datetime-picker.component.scss'],
    standalone: false,
})
export class StandardDatetimePickerComponent implements ControlValueAccessor, OnInit {
    public controlDirective = inject(NgControl, { self: true, optional: true });

    @Input() public label: string;
    @Input() public hint: string;
    @Input() public dateString: string;
    @Input() public dateFormat = 'yyyy-MM-dd';
    @Input() public hourStep = 1;
    @Input() public minuteStep = 1;
    @Input() public secondStep = 1;
    @Input() public seconds = true;
    @Input() public disabled = false;
    @Input() public placement = 'bottom-right';
    @Input() public dateTimePickerType: DateTimePickerType = DateTimePickerType.DateTimePicker;
    @Input() public set focus(value: boolean) {
        this._focus = value;
        this.handleFocus();
    }

    @Output() public valueChangeSubmitted = new EventEmitter();

    public id = getRandomId();
    public datetime: DateTimeModel = new DateTimeModel();
    public DateTimePickerType = DateTimePickerType;

    private _focus: boolean;
    private valueChanged = false;
    private onTouched: () => void = noop;
    private onChange: (_: string) => void = noop;
    @ViewChild('calendar') private calendar: NgbPopover;
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    @ViewChild('datetimepicker', { static: true }) private datetimepicker: any;

    private get isOnlyTime() {
        return this.dateTimePickerType === DateTimePickerType.TimePicker;
    }

    constructor() {
        const config = inject(NgbPopoverConfig);

        config.autoClose = 'outside';
        config.placement = 'auto';

        if (this.controlDirective) {
            this.controlDirective.valueAccessor = this;
        }
    }

    public ngOnInit(): void {
        const inputElement = this.datetimepicker.nativeElement as HTMLInputElement;
        inputElement.maxLength = this.dateFormat.length;

        this.valueChanged = false;

        this.controlDirective.control.addValidators([this.validateDate.bind(this)]);
        this.controlDirective.control.updateValueAndValidity();
    }

    public validateDate(control: AbstractControl) {
        if (control.value !== undefined && control.value?.length) {
            const date = new Date(this.dateString);
            const isValid = !isNaN(date.valueOf());

            return isValid && control.value !== 'Invalid Date' ? null : { invalidDate: true };
        }

        return null;
    }

    public writeValue(newModel: string) {
        if (newModel) {
            // TODO: We need to make sure that we show localtime to the user but provide the UTC timestamp to the parent component
            // backend should always operate on utc timestamps
            this.datetime = Object.assign(this.datetime, DateTimeModel.fromLocalString(newModel));
            this.dateString = newModel;
            this.setDateStringModel();
        } else {
            this.datetime = new DateTimeModel();
        }
    }

    public registerOnChange(fn: () => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    public onInputChange($event: Event) {
        let inputValue = ($event.target as HTMLInputElement).value;

        if (this.isOnlyTime) {
            const { value, invalid } = this.convertTimeToDateString(inputValue);
            if (invalid) {
                return;
            }

            inputValue = value;
        }

        const dateTimeModel = DateTimeModel.fromLocalString(inputValue);
        if (dateTimeModel) {
            this.datetime = dateTimeModel;
            this.setDateStringModel();
        } else if ((inputValue ?? '').trim() === '') {
            this.datetime = new DateTimeModel();
            this.dateString = '';
            this.valueChanged = true;
            this.onChange(this.dateString);
        } else {
            this.valueChanged = true;
            this.onChange(inputValue);
        }
    }

    public onDateChange($event: string | NgbDateStruct, datepicker: NgbDatepicker) {
        const date = new DateTimeModel($event);

        if (!date) {
            this.dateString = this.dateString;
            return;
        }

        if (!this.datetime) {
            this.datetime = date;
        }

        this.datetime.year = date.year;
        this.datetime.month = date.month;
        this.datetime.day = date.day;

        const adjustedDate = new Date(this.datetime.toString());
        if (this.datetime.timeZoneOffset !== adjustedDate.getTimezoneOffset()) {
            this.datetime.timeZoneOffset = adjustedDate.getTimezoneOffset();
        }

        this.setDateStringModel();
    }

    public onTimeChange(event: NgbTimeStruct) {
        this.datetime.hour = event.hour;
        this.datetime.minute = event.minute;
        this.datetime.second = event.second;

        this.setDateStringModel();
    }

    public setDateStringModel() {
        this.dateString = moment(this.datetime.toString()).format();

        this.valueChanged = true;
        this.onChange(this.dateString);
    }

    public inputBlur() {
        this.onTouched();
    }

    public keyupEnter() {
        if (this.valueChanged) {
            this.valueChangeSubmitted.emit();
            this.valueChanged = false;
        }
    }

    public closeCalendar() {
        this.calendar.close();

        if (this.valueChanged) {
            this.valueChangeSubmitted.emit();
            this.valueChanged = false;
        }
    }

    private handleFocus() {
        if (this._focus) {
            this.datetimepicker?._elRef?.nativeElement?.focus();
        }
    }

    public refreshDatepicker(event: NgbDatepickerNavigateEvent, datepicker: NgbDatepicker) {
        if (event.current == null) {
            datepicker.navigateTo(this.datetime);
        }
    }

    private convertTimeToDateString(time: string) {
        const newValue = new Date();
        if (time == null) {
            return { value: newValue.toISOString(), invalid: false };
        }

        const [hour, minute, second = '00'] = time.split(':');
        const invalid = [parseInt(hour), parseInt(minute), parseInt(second)].some((value) => isNaN(value));

        if (invalid) {
            this.controlDirective.control.setErrors({ invalidTime: true });
            return { value: null, invalid: invalid };
        }

        newValue.setHours(parseInt(hour), parseInt(minute), parseInt(second));
        return { value: newValue.toISOString(), invalid: false };
    }
}
